import { useEffect, useState, useRef } from 'react';

function getNavigatorOnline(): boolean {
  if (typeof navigator === 'undefined') return true; // assume online during SSR
  // Some browsers can return undefined-ish; coerce to boolean
  return !!navigator.onLine;
}

export default function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState<boolean>(getNavigatorOnline());

  // Track mount state
  const isMounted = useRef<boolean>(false);

  useEffect(() => {
    // Set to true on mount
    isMounted.current = true;

    // Set to false on unmount
    return () => {
      isMounted.current = false;
    };
  }, []);

  const update = () => {
    const ok = getNavigatorOnline();
    // Use ref to track component mount state
    if (isMounted.current) {
      setIsOnline(ok);
    }
  };

  useEffect(() => {
    update();

    const onOnline = () => {
      if (isMounted.current) {
        setIsOnline(true);
      }
    };
    const onOffline = () => {
      if (isMounted.current) {
        setIsOnline(false);
      }
    };
    const onResume = () => update(); // catch Safari/PWA resumes

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('pageshow', onResume);
    document.addEventListener('visibilitychange', onResume);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('pageshow', onResume);
      document.removeEventListener('visibilitychange', onResume);
    };
  }, []);

  return isOnline;
}
